"use client";

import { DragEvent, FormEvent, useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { uploadRemoveBgImage, isRemoveBgJob } from "@/utils/removebg/api";
import { formatBytes, validateImageFile, RemoveBgValidationError, ACCEPTED_MIME_TYPES } from "@/utils/removebg/validators";
import type { RemoveBgUploadResult } from "@/types/removebg";

interface RemoveBgUploadProps {
  onComplete: (payload: RemoveBgUploadResult) => void | Promise<void>;
  onError?: (message: string) => void;
  isPersisting?: boolean;
}

const ACCEPTED_INPUT = ACCEPTED_MIME_TYPES.join(",");

export default function RemoveBgUpload({ onComplete, onError, isPersisting = false }: RemoveBgUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const resetState = () => {
    setErrorMessage(null);
  };

  const handleError = useCallback(
    (message: string) => {
      setErrorMessage(message);
      onError?.(message);
    },
    [onError]
  );

  const processFile = useCallback(
    async (file: File) => {
      resetState();
      try {
        validateImageFile(file);
      } catch (validationError) {
        if (validationError instanceof RemoveBgValidationError) {
          handleError(validationError.message);
        } else {
          handleError("Arquivo inválido. Verifique o formato e tente novamente.");
        }
        return;
      }

      setSelectedFileName(file.name);
      setIsUploading(true);

      try {
        const response = await uploadRemoveBgImage(file);

        if (isRemoveBgJob(response)) {
          await onComplete({ job: response, file });
        } else if (response.status === "processing") {
          handleError(response.message);
        } else {
          handleError("Resposta inesperada do servidor.");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Não foi possível processar sua imagem.";
        handleError(message);
      } finally {
        setIsUploading(false);
      }
    },
    [handleError, onComplete]
  );

  const handleInputChange = useCallback(
    async (event: FormEvent<HTMLInputElement>) => {
      const fileList = event.currentTarget.files;
      const file = fileList && fileList.length > 0 ? fileList[0] : null;
      if (file) {
        event.currentTarget.value = "";
        await processFile(file);
      }
    },
    [processFile]
  );

  const handleDrop = useCallback(
  async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files?.item(0);
      if (file) {
        await processFile(file);
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="rounded-3xl border border-white/5 bg-white/5 p-10 shadow-soft backdrop-blur-xl">
      <div
        className={clsx(
          "relative flex cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/20 px-6 py-16 text-center transition",
          isDragging ? "border-brand-secondary bg-brand-secondary/10" : "hover:border-brand-secondary/60 hover:bg-white/10"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <p className="rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/60">
          Upload seguro
        </p>
        <h2 className="text-2xl font-semibold text-white">Arraste sua imagem ou clique para selecionar</h2>
        <p className="max-w-xl text-sm text-white/60">
          Formatos suportados: PNG, JPG, WEBP • Tamanho máximo: {formatBytes(100 * 1024 * 1024)}
        </p>

        <button
          type="button"
          className="mt-6 rounded-full bg-brand-primary px-6 py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? "Processando imagem..." : "Selecionar arquivo"}
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_INPUT}
          className="hidden"
          onChange={handleInputChange}
        />

        {selectedFileName && (
          <p className="mt-6 text-sm text-white/70">Arquivo selecionado: {selectedFileName}</p>
        )}

        {errorMessage && (
          <p className="mt-4 text-sm font-medium text-red-300">{errorMessage}</p>
        )}

        {(isUploading || isPersisting) && (
          <div className="mt-8 flex w-full max-w-md flex-col gap-2 text-sm text-white/70">
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-full animate-pulse bg-brand-secondary" />
            </div>
            <span>
              {isUploading ? "Enviando para processamento..." : "Sincronizando histórico local..."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
